// Live updates (SSE /api/events). Events carry full views, so the cache is patched in place; lists are refetched
// because a status change can move a thread between filters.
import type { LiveEvent, ThreadDetail } from "@nerves/shared";
import type { QueryClient } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";
import { keys } from "./queries";

let source: EventSource | null = null;
let connected = true;
const listeners = new Set<() => void>();

function setConnected(value: boolean) {
  if (connected === value) return;
  connected = value;
  for (const l of listeners) l();
}

/** False while the stream is down (EventSource reconnects by itself). */
export function useLiveConnected(): boolean {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => {
        listeners.delete(l);
      };
    },
    () => connected,
  );
}

function patchThread(qc: QueryClient, threadId: string, patch: (t: ThreadDetail) => ThreadDetail) {
  for (const [key, data] of qc.getQueriesData<ThreadDetail>({ queryKey: keys.threadAll })) {
    if (data?.id === threadId) qc.setQueryData(key, patch(data));
  }
}

function apply(qc: QueryClient, ev: LiveEvent) {
  switch (ev.kind) {
    case "thread.upserted":
      patchThread(qc, ev.thread.id, (t) => ({ ...t, ...ev.thread, messages: t.messages }));
      void qc.invalidateQueries({ queryKey: keys.threadsAll });
      void qc.invalidateQueries({ queryKey: keys.me });
      return;
    case "message.created":
      patchThread(qc, ev.message.threadId, (t) =>
        t.messages.some((x) => x.id === ev.message.id) ? t : { ...t, messages: [...t.messages, ev.message] },
      );
      return;
    case "message.updated":
      patchThread(qc, ev.message.threadId, (t) => ({
        ...t,
        messages: t.messages.map((x) => (x.id === ev.message.id ? ev.message : x)),
      }));
      return;
    case "liveness.changed":
      patchThread(qc, ev.threadId, (t) => ({ ...t, listening: ev.listening }));
      for (const [key, data] of qc.getQueriesData<{ threads: Array<{ id: string; listening: boolean }> }>({
        queryKey: keys.threadsAll,
      })) {
        if (data?.threads.some((t) => t.id === ev.threadId)) {
          qc.setQueryData(key, {
            ...data,
            threads: data.threads.map((t) => (t.id === ev.threadId ? { ...t, listening: ev.listening } : t)),
          });
        }
      }
      return;
  }
}

export function startLive(qc: QueryClient): void {
  if (source) return;
  source = new EventSource("/api/events");
  const kinds: LiveEvent["kind"][] = ["thread.upserted", "message.created", "message.updated", "liveness.changed"];
  for (const kind of kinds) {
    source.addEventListener(kind, (e) => {
      try {
        apply(qc, JSON.parse((e as MessageEvent<string>).data) as LiveEvent);
      } catch (err) {
        console.error("[live] bad event", err);
      }
    });
  }
  source.addEventListener("open", () => {
    // Anything may have changed while we were away.
    if (!connected) void qc.invalidateQueries();
    setConnected(true);
  });
  source.addEventListener("error", () => setConnected(false));
}

export function stopLive(): void {
  source?.close();
  source = null;
}
